import useStore from "../../../store"
import Spinner from "../../common/Spinner"
import { useState, useCallback, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { NeutralButton } from "../../common/Buttons"
import ImagePreview from "./ImagePreview"
import NAPConfirmationDetails from "./NAPConfirmationDetails"
import SwipeAnimation from "../../common/SwipeAnimation"
import rollbarLogger from "../../../utilities/rollbarLogger"
import ImagePreviewGrid from "../../common/ImagePreviewGrid"
import { createFileFromBase64 } from "../../../utilities/createFileFromBase64"
import { useIsMobile } from "../../../hooks/useIsMobile"

export default function NAPConfirmation({ issues }) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const {
    setErrMessage,
    NAPBlackbook: blackbook,
    createFloorRequest,
    NAPFloorAmount: floorAmount,
    setNapId,
    updateUnit,
    NAPImages: images,
    NAPBankAccount,
    NAPRequiresBankPayoff,
    setCurrentStep,
    setEditingNapPhoto,
    user,
    nextStep,
    dealer,
    submittingNap
  } = useStore()

  const rollbar = rollbarLogger(user)

  const [previewImage, setPreviewImage] = useState(null)

  const submitFloor = useCallback(async () => {
    useStore.setState({ submittingNap: true })
    let photoCount = 0
    let totalPhotoBytes = 0
    try {
      const formData = new FormData()
      for(const [key, value] of Object.entries(images)) {
        const file = await createFileFromBase64(key, value)

        photoCount += 1
        totalPhotoBytes += file.size
        formData.append('unit[nap_photos_ary][]', file)
      }

      formData.append('unit[vin]', blackbook.vin)
      formData.append('unit[principal]', floorAmount)
      formData.append('unit[mileage]', blackbook.mileage)
      formData.append('unit[model]', `${blackbook.model} ${blackbook.series}`)
      formData.append('dealer_id', dealer?.id)

      if (blackbook.isManualEntry) {
        formData.append('unit[make]', blackbook.make)
        formData.append('unit[year]', blackbook.model_year)
      }

      if (NAPRequiresBankPayoff) {
        formData.append('unit[bank_account_id]', null)
      } else {
        formData.append('unit[bank_account_id]', NAPBankAccount?.id)
      }

      const response = await createFloorRequest(formData)
      if (response.ok) {
        const data = await response.json()
        if (data?.data?.id) {
          setNapId(data.data.id)
          nextStep()
        } else {
          setErrMessage("NAP submitted, but response is missing an ID.")
          rollbar.logError("Missing ID in response: " + JSON.stringify(data))
        }
      } else if (response.status === 422) {
        const data = await response.json()
        const error = data?.errors?.message || 'There was an error requesting this floor'
        setErrMessage(error)
        rollbar.logError(error, { photoCount, totalPhotoBytes, status: response.status })
      } else {
        setErrMessage('There was an error requesting this floor')
        rollbar.logError('Floor request failed', { photoCount, totalPhotoBytes, status: response.status })
      }
    } catch (err) {
      setErrMessage('Unable to reach server')
      rollbar.logError(err, {
        context: 'submitFloor upload',
        photoCount,
        totalPhotoBytes,
        errorName: err?.name,
        errorMessage: err?.message
      })
    } finally {
      useStore.setState({ submittingNap: false })
    }
  }, [floorAmount, blackbook])

  const updateFloor = async () => {
    useStore.setState({ submittingNap: true })
    let photoCount = 0
    let totalPhotoBytes = 0
    try {
      const formData = new FormData()
      for(const [key, value] of Object.entries(images)) {
        const file = await createFileFromBase64(key, value)

        photoCount += 1
        totalPhotoBytes += file.size
        formData.append('unit[nap_photos_ary][]', file)
      }
      formData.append('dealer_id', dealer.id)

      const response = await updateUnit(blackbook?.id, formData)

      if (response.ok) {
        const data = await response.json()
        setNapId(data.data.id)
        nextStep()
      } else if (response.status === 422) {
        const data = await response.json()
        const error = data?.errors?.message || 'There was an error requesting this floor'
        setErrMessage(error)
        rollbar.logError(error, { photoCount, totalPhotoBytes, status: response.status })
      } else {
        setErrMessage('There was an error requesting this floor')
        rollbar.logError('Floor update failed', { photoCount, totalPhotoBytes, status: response.status })
      }
    } catch (err) {
      setErrMessage('Unable to reach server')
      rollbar.logError(err, {
        context: 'updateFloor upload',
        photoCount,
        totalPhotoBytes,
        errorName: err?.name,
        errorMessage: err?.message
      })
    } finally {
      useStore.setState({ submittingNap: false })
    }
  }

  const handleEditNapPhoto = (index) => {
    if (issues) {
      setCurrentStep(index)
    } else {
      setCurrentStep(index + 1)
    }
    setEditingNapPhoto(true)
  }

  return(
    <SwipeAnimation>
      <div className="relative flex flex-col justify-between items-center pt-6 rounded w-full h-full">

        <NAPConfirmationDetails issues={issues} />

        <ImagePreviewGrid
          imageUrls={images}
          onClick={(key) => setPreviewImage([key, images[key]])}
          onEditClick={handleEditNapPhoto}
          gridCols={isMobile ? "grid-cols-3" : "grid-cols-4"}
        />

        <div className="my-4">
          {submittingNap ?
            <Spinner height="45" /> :
            <NeutralButton
              text={issues ? t("Update Floor Request") : t("Submit Floor Request")}
              onClick={issues ? updateFloor : submitFloor}
              style="text-lg"
            />
          }
        </div>
      </div>

      <div className="fixed top-0 left-0 z-50">
        {previewImage && <ImagePreview
          image={images[previewImage[0]]}
          onClose={() => setPreviewImage(null)}
        />}
      </div>
    </SwipeAnimation>
  )
}